import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';

/** Dispatched (bubbled on window) while the main request/response splitter drags so Monaco can layout. */
export const MONACO_LAYOUT_FLUSH_EVENT = 'postman-alt:monaco-layout-flush';

/**
 * Clamp first-panel split % so first/second panels are at least minPx tall/wide (vertical/horizontal).
 * @param {number} splitPct - current first panel % (0–100)
 * @param {number} containerPx - container height (vertical) or width (horizontal)
 */
function clampSplitToPixelMinimums(
  splitPct,
  containerPx,
  minSizePct,
  maxSizePct,
  minFirstPanelPx,
  minSecondPanelPx
) {
  let lower = minSizePct;
  let upper = maxSizePct;
  if (containerPx > 0) {
    if (minFirstPanelPx != null && minFirstPanelPx > 0) {
      lower = Math.max(lower, (minFirstPanelPx / containerPx) * 100);
    }
    if (minSecondPanelPx != null && minSecondPanelPx > 0) {
      upper = Math.min(upper, 100 - (minSecondPanelPx / containerPx) * 100);
    }
  }
  if (upper >= lower) {
    return Math.max(lower, Math.min(upper, splitPct));
  }
  return (lower + upper) / 2;
}

const ResizableSplitter = ({
  children,
  direction = 'horizontal', // 'horizontal' or 'vertical'
  initialSplit = 35, // percentage for first panel
  minSize = 0, // minimum percentage for each panel (0 = can collapse completely)
  maxSize = 100, // maximum percentage for first panel (100 = can take full space)
  /** When set, first panel will not shrink below this many CSS pixels (height if vertical, width if horizontal). */
  minFirstPanelPx = undefined,
  /** When set, second panel will not shrink below this many CSS pixels. */
  minSecondPanelPx = undefined,
  resizerStyle = {},
  className = '',
  /** Fired once when a drag ends (mouseup after mousedown on the resizer). Lets children sync layout (e.g. Monaco). */
  onResizeEnd = undefined,
}) => {
  // Validate props
  if (minSize < 0 || maxSize > 100 || minSize > maxSize) {
    console.warn('ResizableSplitter: Invalid minSize/maxSize values. minSize should be >= 0, maxSize should be <= 100, and minSize should be <= maxSize');
  }
  
  if (initialSplit < minSize || initialSplit > maxSize) {
    console.warn(`ResizableSplitter: initialSplit (${initialSplit}) is outside the bounds of minSize (${minSize}) and maxSize (${maxSize})`);
  }
  // Ensure initial split is within valid bounds
  const validatedInitialSplit = Math.max(minSize, Math.min(maxSize, initialSplit));
  const [split, setSplit] = useState(validatedInitialSplit);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const resizerRef = useRef(null);
  const liveLayoutRafRef = useRef(null);

  const scheduleMonacoLayoutFlush = useCallback(() => {
    if (liveLayoutRafRef.current != null) return;
    liveLayoutRafRef.current = requestAnimationFrame(() => {
      liveLayoutRafRef.current = null;
      window.dispatchEvent(new CustomEvent(MONACO_LAYOUT_FLUSH_EVENT));
    });
  }, []);

  // Apply initialSplit (and % bounds); when pixel mins are set, convert using current container size
  useLayoutEffect(() => {
    const el = containerRef.current;
    let next = Math.max(minSize, Math.min(maxSize, initialSplit));
    if (el != null && (minFirstPanelPx != null || minSecondPanelPx != null)) {
      const rect = el.getBoundingClientRect();
      const dim = direction === 'vertical' ? rect.height : rect.width;
      if (dim >= 1) {
        next = clampSplitToPixelMinimums(
          next,
          dim,
          minSize,
          maxSize,
          minFirstPanelPx,
          minSecondPanelPx
        );
      }
    }
    setSplit(next);
  }, [initialSplit, minSize, maxSize, minFirstPanelPx, minSecondPanelPx, direction]);

  // After user drags / other updates: keep split within pixel mins when container resizes
  useLayoutEffect(() => {
    if (minFirstPanelPx == null && minSecondPanelPx == null) {
      return undefined;
    }
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const applyClamp = () => {
      const rect = el.getBoundingClientRect();
      const dim = direction === 'vertical' ? rect.height : rect.width;
      if (dim < 1) return;
      setSplit((s) => {
        const next = clampSplitToPixelMinimums(
          s,
          dim,
          minSize,
          maxSize,
          minFirstPanelPx,
          minSecondPanelPx
        );
        return next === s ? s : next;
      });
    };
    applyClamp();
    const ro = new ResizeObserver(applyClamp);
    ro.observe(el);
    return () => ro.disconnect();
  }, [direction, minSize, maxSize, minFirstPanelPx, minSecondPanelPx]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    let newSplit;
    if (direction === 'horizontal') {
      const containerWidth = rect.width;
      const mouseX = e.clientX - rect.left;
      newSplit = (mouseX / containerWidth) * 100;
    } else {
      const containerHeight = rect.height;
      const mouseY = e.clientY - rect.top;
      newSplit = (mouseY / containerHeight) * 100;
    }

    newSplit = Math.max(minSize, Math.min(maxSize, newSplit));

    const containerPx = direction === 'horizontal' ? rect.width : rect.height;
    newSplit = clampSplitToPixelMinimums(
      newSplit,
      containerPx,
      minSize,
      maxSize,
      minFirstPanelPx,
      minSecondPanelPx
    );

    setSplit(newSplit);
    scheduleMonacoLayoutFlush();
  }, [
    isDragging,
    direction,
    minSize,
    maxSize,
    minFirstPanelPx,
    minSecondPanelPx,
    scheduleMonacoLayoutFlush,
  ]);

  const handleMouseUp = useCallback(() => {
    if (liveLayoutRafRef.current != null) {
      cancelAnimationFrame(liveLayoutRafRef.current);
      liveLayoutRafRef.current = null;
    }
    window.dispatchEvent(new CustomEvent(MONACO_LAYOUT_FLUSH_EVENT));
    setIsDragging((wasDragging) => {
      if (wasDragging) {
        onResizeEnd?.();
      }
      return false;
    });
  }, [onResizeEnd]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp, direction]);

  const [firstChild, secondChild] = React.Children.toArray(children);

  const containerStyle = {
    display: 'flex',
    flexDirection: direction === 'horizontal' ? 'row' : 'column',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    minHeight: 0
  };

  const firstPanelStyle = {
    [direction === 'horizontal' ? 'width' : 'height']: `${split}%`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  };

  const secondPanelStyle = {
    [direction === 'horizontal' ? 'width' : 'height']: `${100 - split}%`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  };

  const defaultResizerStyle = {
    flexShrink: 0,
    cursor: direction === 'horizontal' ? 'col-resize' : 'row-resize',
    position: 'relative',
    zIndex: 10,
  };

  const mergedResizerStyle = { ...defaultResizerStyle, ...resizerStyle };

  return (
    <div ref={containerRef} className={className} style={containerStyle}>
      <div style={firstPanelStyle}>
        {firstChild}
      </div>
      <div
        ref={resizerRef}
        style={mergedResizerStyle}
        onMouseDown={handleMouseDown}
        className={`resizer resizer--${direction} ${isDragging ? 'dragging' : ''}`}
      />
      <div style={secondPanelStyle}>
        {secondChild}
      </div>
    </div>
  );
};

export default ResizableSplitter;
